/**
 * AdvancedPatternStep Component - For ADVANCED pattern configuration
 * Used when user selected "I want to build something custom" or needs advanced macro functionality
 */

import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Settings, 
  Wand2, 
  CheckCircle2, 
  ArrowRight, 
  Lightbulb, 
  AlertTriangle,
  Copy,
  Plus
} from 'lucide-react'
import { WizardState, Platform, MacroPattern } from '../../../../types/macros'
import PatternPreview from './shared/PatternPreview'

export interface AdvancedPatternStepProps {
  wizardState: WizardState
  onUpdateState: (updates: Partial<WizardState>) => void
  onBack: () => void
}

const AdvancedPatternStep: React.FC<AdvancedPatternStepProps> = ({
  wizardState,
  onUpdateState,
  onBack
}) => {
  const [localState, setLocalState] = useState({
    customSyntax: wizardState.patternData.advanced?.customSyntax || '',
    guidedConfig: wizardState.patternData.advanced?.guidedConfig || {
      dataKeys: [''],
      operators: [],
      conditions: []
    },
    description: wizardState.patternData.advanced?.description || ''
  })

  // Update parent state when local state changes
  React.useEffect(() => {
    onUpdateState({
      patternData: {
        ...wizardState.patternData,
        advanced: {
          mode: 'guided',
          customSyntax: localState.customSyntax,
          guidedConfig: localState.guidedConfig,
          description: localState.description
        }
      }
    })
  }, [localState, onUpdateState, wizardState.patternData])

  // Platform-specific syntax examples
  const syntaxExamples = React.useMemo(() => {
    const platform = wizardState.platform
    
    const examples = {
      [Platform.SHOPIFY]: [
        'Simple: {{customer.first_name}}',
        'With fallback: {{customer.first_name | ' + '"Guest"' + '}}',
        'Conditional: {{#if customer.tags contains ' + '"VIP"' + '}}VIP Member{{/if}}',
        'Loop: {{#each order.line_items}}{{title}} - {{price}}{{/each}}'
      ],
      [Platform.MOMENTPERKS]: [
        'User data: {{user.name}}',
        'Points: {{user.points | ' + '"0"' + '}} points available',
        'Tier check: {{#if user.tier == ' + '"gold"' + '}}Gold Benefits{{else}}Standard Benefits{{/if}}',
        'Offers: {{#each user.available_offers}}{{title}}{{/each}}'
      ],
      [Platform.PERKSWALL]: [
        'Member info: {{member.display_name}}',
        'Balance: {{member.reward_balance | ' + '"No rewards"' + '}}',
        'Status: {{#if member.is_active}}Active Member{{else}}Join Today{{/if}}',
        'Recent: {{#each member.recent_rewards}}{{reward_name}}{{/each}}'
      ]
    }
    
    return examples[platform || Platform.MOMENTPERKS] || examples[Platform.MOMENTPERKS]
  }, [wizardState.platform])

  // Common operators and helpers
  const operators = [
    { name: 'if', syntax: '{{#if condition}}...{{/if}}', description: 'Conditional display' },
    { name: 'unless', syntax: '{{#unless condition}}...{{/unless}}', description: 'Negative conditional' },
    { name: 'each', syntax: '{{#each array}}...{{/each}}', description: 'Loop through items' },
    { name: 'with', syntax: '{{#with object}}...{{/with}}', description: 'Set context' },
    { name: 'eq', syntax: '{{#if (eq a b)}}...{{/if}}', description: 'Equals comparison' },
    { name: 'gt/lt', syntax: '{{#if (gt a b)}}...{{/if}}', description: 'Greater/less than' },
    { name: 'and/or', syntax: '{{#if (and a b)}}...{{/if}}', description: 'Logical operators' }
  ]

  // Syntax validation
  const syntaxValidation = React.useMemo(() => {
    if (!localState.customSyntax.trim()) return { isValid: true, errors: [] }
    
    const errors = []
    const syntax = localState.customSyntax
    
    // Check for balanced braces
    const openBraces = (syntax.match(/{{/g) || []).length
    const closeBraces = (syntax.match(/}}/g) || []).length
    if (openBraces !== closeBraces) {
      errors.push('Mismatched braces {{ }}')
    }
    
    // Check for balanced conditionals
    const ifCount = (syntax.match(/{{#if/g) || []).length
    const endIfCount = (syntax.match(/{{\/if}}/g) || []).length
    if (ifCount !== endIfCount) {
      errors.push('Mismatched {{#if}} and {{/if}} tags')
    }
    
    // Check for balanced loops
    const eachCount = (syntax.match(/{{#each/g) || []).length
    const endEachCount = (syntax.match(/{{\/each}}/g) || []).length
    if (eachCount !== endEachCount) {
      errors.push('Mismatched {{#each}} and {{/each}} tags')
    }
    
    // Check for common mistakes
    if (syntax.includes('{{{') || syntax.includes('}}}')) {
      errors.push('Use {{}} not {{{}}}')
    }
    
    if (syntax.includes('{{if') && !syntax.includes('{{#if')) {
      errors.push('Use {{#if}} not {{if}}')
    }
    
    return { isValid: errors.length === 0, errors }
  }, [localState.customSyntax])

  // Validation
  const isValid = React.useMemo(() => {
    return localState.customSyntax.trim() !== '' && syntaxValidation.isValid
  }, [localState.customSyntax, syntaxValidation.isValid])

  const copyExample = (example: string) => {
    navigator.clipboard.writeText(example)
    // Could add a toast notification here
  }

  const previewTestData = React.useMemo(() => {
    const keys = Array.from(new Set((localState.customSyntax.match(/{{\s*([a-zA-Z0-9_.]+)/g) || [])
      .map((m) => m.replace('{{', '').trim())
      .filter((k) => k && !k.startsWith('#') && !k.startsWith('/'))))

    const sampleData: Record<string, string> = {
      user_name: 'Sarah',
      user_name_missing: '',
      first_name: 'Sarah',
      name: 'Sarah',
      city: 'New York',
      points: '500',
      tier: 'gold'
    }

    keys.forEach((key) => {
      if (!sampleData[key]) {
        sampleData[key] = 'Sample Value'
      }
    })

    return sampleData
  }, [localState.customSyntax])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-full mb-4">
          <Settings className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-[#1A2B49] dark:text-white mb-2">
          Advanced Pattern Setup
        </h2>
        <p className="text-[#6E6D7A] dark:text-gray-400 max-w-md mx-auto">
          Build custom macros with full template engine power. Use conditions, loops, and advanced logic.
        </p>
      </div>

      {/* Pattern Confirmation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-purple-800 dark:text-purple-300 mb-1">
              You Selected: Advanced Pattern (Custom Macros)
            </h3>
            <p className="text-sm text-purple-700 dark:text-purple-400">
              Design complex macros with conditionals, loops, and custom logic. 
              Perfect for sophisticated personalization and dynamic content.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Guided Builder */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-6"
        >
          {/* Guided Builder Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Wand2 className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-300">
                Visual Macro Builder
              </h3>
            </div>
          </div>

          {/* Building Blocks */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Visual Builder */}
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Quick Building Blocks</h4>
                
                {/* Quick Actions */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <button
                    onClick={() => {
                      const newSyntax = localState.customSyntax + '{{user_name}}'
                      setLocalState(prev => ({ ...prev, customSyntax: newSyntax }))
                    }}
                    className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-left hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                  >
                    <div className="font-medium text-blue-900 dark:text-blue-100">Add Data Field</div>
                    <div className="text-sm text-blue-700 dark:text-blue-300">{'{{field_name}}'}</div>
                  </button>
                  
                  <button
                    onClick={() => {
                      const newSyntax = localState.customSyntax + '{{#if condition}}content{{/if}}'
                      setLocalState(prev => ({ ...prev, customSyntax: newSyntax }))
                    }}
                    className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-left hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                  >
                    <div className="font-medium text-green-900 dark:text-green-100">Add Condition</div>
                    <div className="text-sm text-green-700 dark:text-green-300">{'{{#if}}...{{/if}}'}</div>
                  </button>
                  
                  <button
                    onClick={() => {
                      const newSyntax = localState.customSyntax + '{{field_name | ' + '"default_value"' + '}}'
                      setLocalState(prev => ({ ...prev, customSyntax: newSyntax }))
                    }}
                    className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg text-left hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                  >
                    <div className="font-medium text-purple-900 dark:text-purple-100">Add Fallback</div>
                    <div className="text-sm text-purple-700 dark:text-purple-300">{'{{field | ' + '"default"' + '}}'}</div>
                  </button>
                  
                  <button
                    onClick={() => {
                      const newSyntax = localState.customSyntax + '{{#each items}}{{name}}{{/each}}'
                      setLocalState(prev => ({ ...prev, customSyntax: newSyntax }))
                    }}
                    className="p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg text-left hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors"
                  >
                    <div className="font-medium text-orange-900 dark:text-orange-100">Add Loop</div>
                    <div className="text-sm text-orange-700 dark:text-orange-300">{'{{#each}}...{{/each}}'}</div>
                  </button>
                </div>

                {/* Advanced Operators */}
                <div>
                  <h5 className="font-medium text-gray-800 dark:text-gray-200 mb-3">Advanced Operations</h5>
                  <div className="space-y-2">
                    {operators.map((operator, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          const newSyntax = localState.customSyntax + operator.syntax
                          setLocalState(prev => ({ ...prev, customSyntax: newSyntax }))
                        }}
                        className="w-full p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-mono text-sm text-gray-900 dark:text-gray-100">{operator.syntax}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{operator.description}</div>
                          </div>
                          <Plus className="w-4 h-4 text-gray-400" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Platform Examples */}
                <div>
                  <h5 className="font-medium text-gray-800 dark:text-gray-200 mb-3">Platform Examples</h5>
                  <div className="space-y-2">
                    {syntaxExamples.map((example, index) => (
                      <button
                        key={index}
                        onClick={() => setLocalState(prev => ({ ...prev, customSyntax: example.split(': ')[1] }))}
                        className="w-full p-3 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border border-indigo-200 dark:border-indigo-800 rounded text-left hover:shadow-md transition-all"
                      >
                        <div className="text-sm text-indigo-900 dark:text-indigo-100">{example}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Live Preview */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-gray-900 dark:text-white">Live Macro Code</h4>
                <div className="flex gap-2">
                  {localState.customSyntax && (
                    <button
                      onClick={() => copyExample(localState.customSyntax)}
                      className="flex items-center gap-1 px-3 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                    >
                      <Copy className="w-3 h-3" />
                      Copy
                    </button>
                  )}
                </div>
              </div>
              
              <div className="bg-gray-900 rounded-lg p-4 min-h-[300px]">
                <div className="text-green-400 font-mono text-sm whitespace-pre-wrap break-all">
                  {localState.customSyntax || '// Click the building blocks above to start creating your macro\n// Your generated code will appear here in real-time'}
                </div>
              </div>

              {/* Syntax Validation */}
              {localState.customSyntax && (
                <div className={`p-3 rounded-lg ${
                  syntaxValidation.isValid 
                    ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
                }`}>
                  <div className="flex items-center gap-2">
                    {syntaxValidation.isValid ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                    )}
                    <span className="text-sm font-medium">
                      {syntaxValidation.isValid ? 'Syntax is valid!' : 'Syntax errors found:'}
                    </span>
                  </div>
                  {!syntaxValidation.isValid && (
                    <ul className="mt-1 text-sm space-y-1">
                      {syntaxValidation.errors.map((error, index) => (
                        <li key={index}>• {error}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Quick Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => setLocalState(prev => ({ ...prev, customSyntax: '' }))}
                  className="flex-1 p-2 text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  Clear All
                </button>
                {localState.customSyntax && (
                  <button
                    onClick={() => {
                      const textarea = document.createElement('textarea')
                      textarea.value = localState.customSyntax
                      document.body.appendChild(textarea)
                      textarea.select()
                      document.execCommand('copy')
                      document.body.removeChild(textarea)
                    }}
                    className="flex-1 p-2 text-sm bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                  >
                    Copy Code
                  </button>
                )}
              </div>

              {/* Inline Rendered Preview */}
              <PatternPreview
                pattern={MacroPattern.ADVANCED}
                syntax={localState.customSyntax}
                showBothScenarios={true}
                testData={previewTestData}
              />
            </div>
          </div>
        </motion.div>

      {/* Description */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <label className="block text-sm font-semibold text-[#1A2B49] dark:text-white mb-2">
          Description (Optional)
        </label>
        <textarea
          value={localState.description}
          onChange={(e) => setLocalState(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Describe what this advanced macro does..."
          rows={3}
          className="w-full p-3 border-2 border-gray-200 focus:border-[#FE4215] dark:border-gray-700 dark:focus:border-[#FE4215] rounded-lg focus:outline-none bg-white dark:bg-gray-800 text-[#1A2B49] dark:text-white resize-none"
        />
      </div>

      {/* Help Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4"
      >
        <div className="flex items-start gap-3">
          <Lightbulb className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-2">
              Advanced Pattern Tips
            </h4>
            <div className="text-sm text-yellow-700 dark:text-yellow-400 space-y-1">
              <p>• Test your macros thoroughly with different data scenarios</p>
              <p>• Use meaningful variable names for better maintainability</p>
              <p>• Consider fallback values for optional data fields</p>
              <p>• Document complex logic in the description field</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#6E6D7A] hover:text-[#1A2B49] dark:text-gray-400 dark:hover:text-white transition-colors"
        >
          ← Back to pattern selection
        </button>

        {isValid && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-2 text-sm text-green-600"
          >
            <CheckCircle2 className="w-4 h-4" />
            Ready to preview
            <ArrowRight className="w-4 h-4" />
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default AdvancedPatternStep